import { createFileRoute } from "@tanstack/react-router";
import { BuildingViewerViewMode } from "building-types/geometry/buildingGeometryTypes";
import { FaceCategory, roofTrimFaceCategories, wallFaceCategories } from "building-types/materials/faceCategories";
import type { ViewFilter } from "building-types/views/viewFilterTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { SolidWall } from "building-types/walls/wallTypes";
import { DEBUG_EXPORT_PAGE_WRAPPER_ID } from "common/constants";
import type { UserNotificationType } from "common/types/userNotification";
import { toConstantCase } from "common/utils/strings";
import type { Face } from "geom/brep";
import type { Mesh3 } from "geom/mesh";
import { polyLine2NonUniformScaleAndKeepOrigin } from "geom/polyLine2";
import type { PolyLine3 } from "geom/polyLine3";
import { TriangulationStrategy } from "geom/tess";
import { defaultCameraSettings } from "gfx/CameraSettings";
import {
  facesToTHREE,
  lightIntensityToThreePhysicalLight,
  mesh3ToTHREE,
  planarMesh3NormalToCylinder,
  polyLine3ToCylinder,
} from "gfx/convert";
import { DebugCallbackType } from "gfx/materials/discretization/debug";
import PerspectiveControls from "gfx/PerspectiveControls";
import THREEAssetLoader from "gfx/THREEAssetLoader";
import { VertexNormalsHelper } from "gfx/threejs/examples/jsm/helpers/VertexNormalsHelper";
import { UVVertexShader } from "gfx/threejs/higharc/UVVertexShader";
import THREEViewerLite from "gfx/THREEViewerLite";
import type { THREEViewerSystem } from "gfx/THREEViewerSystem";
import styles from "lib/components/debug/export.module.scss";
import ExportConfigurationViewer from "lib/components/debug/ExportConfigurationViewer";
import exportDebuggerReducer, { ExportDebuggerActionType } from "lib/components/debug/exportDebuggerReducer";
import DebugBuildingViewer from "lib/components/design/DebugBuildingViewer";
import SceneStateHUD from "lib/components/design/SceneStateHUD";
import type { CollapsibleSectionHeaderProps } from "lib/components/studio/CollapsibleSectionHeader";
import { CollapsibleSectionHeader } from "lib/components/studio/CollapsibleSectionHeader";
import { renderStageConstants } from "lib/components/studio/renderStageConstants";
import View3DFilterControls from "lib/components/studio/View3DFilterControls";
import { configurationParse } from "lib/house/export/configuration";
import { downloadExportGLTF } from "lib/house/export/downloadExport";
import type { ExportOptions } from "lib/house/export/export";
import {
  defaultBuildingExportGroupsSelection,
  emptyBuildingExportGroupsSelection,
  defaultExportOptions,
  exportBuildingGLTF,
} from "lib/house/export/export";
import type { ExportConfiguration } from "lib/house/export/exportTypes";
import { defaultExporterView3DFilter } from "lib/house/render/viewFilterDefaults";
import { buildingElevationOptionSet } from "lib/options/buildingElevationOptionSet";
import { setBuilding, setLoading } from "lib/state/customizerActions";
import { LoadState, useCustomizerDispatch, useCustomizerSelector } from "lib/state/customizerContext";
import { setViewFilter, setViewMode } from "lib/state/sceneStateActions";
import withCustomizer from "lib/state/withCustomizer";
import { emptyViewFilter } from "lib/studio/viewFilters";
import getPageTitle from "lib/utils/getPageTitle";
import type { ReactNode } from "react";
import React, { createContext, useContext, useEffect, useReducer, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import type { Material, Object3D } from "three";
import {
  AmbientLight,
  BufferAttribute,
  BufferGeometry,
  DirectionalLight,
  DoubleSide,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Points,
  PointsMaterial,
  Scene,
  ShaderMaterial,
  Vector3,
  WireframeGeometry,
} from "three";
import SplitButton from "ui/buttons/SplitButton";
import Loading from "ui/components/Loading";
import { isUserNotification } from "ui/components/UserNotification";
import useAppContext from "ui/hooks/useAppContext";
import FullScreenBox from "ui/layouts/FullScreenBox";
import withAuthentication from "../../components/withAuthentication";

const exporterViewerDefaultRenderStageFilter: ViewFilter = { ...defaultExporterView3DFilter, grid: true };

const GLTF_SCENE_NAME = "gltf";

const DEBUG_INFO_SCENE_NAME = "debug-info";
const WIREFRAME_NAME = "debug-wireframe";
const VERTEX_NORMALS_NAME = "debug-vertex-normals";
const WORLD_SPACE_NORMAL_MATERIAL_NAME = "debug-world-space-normal";
const WORLD_SPACE_UV_MATERIAL_NAME = "debug-world-space-uv";
const DEBUG_FACE_PREFIX_NAME = "debug-face";
const DEBUG_FACE_VERTICES_NAME = "debug-vertices";
const DEBUG_FACE_NORMALS_NAME = "debug-normals";
const DEBUG_POINT_NAME = "debug-point";
const DEBUG_WALL_NAME = "debug-wall";

const RENDER_ORDER_DEBUG_FACE = 1;
const RENDER_ORDER_DEBUG_MERGED = 2;
const RENDER_ORDER_DEBUG_LINE = 3;
const RENDER_ORDER_DEBUG_POINT = 4;
// Materials used by view
const faceMaterial = new MeshBasicMaterial({
  color: 0xff0000,
  opacity: 0.5,
  transparent: true,
  depthTest: false,
  depthWrite: false,
  polygonOffset: true,
  polygonOffsetFactor: -1,
  side: DoubleSide,
});

const lineMeshMaterial = new MeshBasicMaterial({
  color: 0xa000a0,
  opacity: 0.3,
  transparent: true,
  depthTest: false,
  depthWrite: false,
  polygonOffset: true,
  polygonOffsetFactor: -1,
  side: DoubleSide,
});

const pointsMaterial = new PointsMaterial({
  color: 0x000000,
  opacity: 0.5,
  transparent: true,
  depthTest: false,
  depthWrite: false,
  side: DoubleSide,
  size: 10,
  sizeAttenuation: false,
});

const selectedPointsMaterial = pointsMaterial.clone();
selectedPointsMaterial.color.setHex(0x000000);
selectedPointsMaterial.size = 20;

const worldSpaceNormalMaterial = new ShaderMaterial({
  name: WORLD_SPACE_NORMAL_MATERIAL_NAME,
  vertexShader: `
  varying vec3 vNormal;

  void main() {
    vNormal = normalize(normal);

    vec4 modelViewPosition = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * modelViewPosition;
  }
`,
  fragmentShader: `
  varying vec3 vNormal;

  void main() {
    gl_FragColor = vec4(vNormal * 0.5 + 0.5, 1.0);
  }
`,
});

const worldSpaceUVMaterial = new ShaderMaterial({
  name: WORLD_SPACE_UV_MATERIAL_NAME,
  uniforms: {
    scale: { value: 1.0 },
  },
  vertexShader: UVVertexShader,
  fragmentShader: `
  uniform float scale;

  varying vec2 vUv;

  void main() {
    gl_FragColor = vec4(vUv / scale, 0.0, 1.0);
  }
`,
});

const windingOrderMaterial = new ShaderMaterial({
  name: WORLD_SPACE_UV_MATERIAL_NAME,
  uniforms: {
    scale: { value: 100.0 },
  },
  side: DoubleSide,
  vertexShader: UVVertexShader,
  fragmentShader: `
  void main() {
    if (gl_FrontFacing) {
      gl_FragColor = vec4(0.0, 1.0, 0.0, 1.0);
    } else {
      gl_FragColor = vec4(1.0, 0.0, 0.0, 1.0);
    }
  }
`,
});

// Materials used by DebugInfo
const mergedFaceMaterial = faceMaterial.clone();
mergedFaceMaterial.color.setHex(0x00ff00);

const rectangularFaceMaterial = faceMaterial.clone();
rectangularFaceMaterial.color.setHex(0xa000a0);

const convexFaceMaterial = faceMaterial.clone();
convexFaceMaterial.color.setHex(0x03a0ff);

const concaveFaceMaterial = faceMaterial.clone();
concaveFaceMaterial.color.setHex(0xff0000);

const meshMaterial = new MeshBasicMaterial({
  color: 0xa000a0,
  opacity: 0.3,
  transparent: true,
  depthTest: false,
  depthWrite: false,
  polygonOffset: true,
  polygonOffsetFactor: -1,
  side: DoubleSide,
});

const outsideMeshMaterial = meshMaterial.clone();
outsideMeshMaterial.color.setHex(0xff0000);

const originalMeshMaterial = meshMaterial.clone();
originalMeshMaterial.color.setHex(0x50ff00);

const convexMeshMaterial = meshMaterial.clone();
convexMeshMaterial.color.setHex(0x03a0ff);

const concaveMeshMaterial = meshMaterial.clone();
concaveMeshMaterial.color.setHex(0xff0000);

// Debug view helpers
function addFaceVertices(mesh: Mesh) {
  const faceVerticesMesh = new Points(mesh.geometry, pointsMaterial);
  faceVerticesMesh.name = DEBUG_FACE_VERTICES_NAME;
  mesh.add(faceVerticesMesh);
}

function addFaceNormals(planarMesh: Mesh) {
  const cylinder = planarMesh3NormalToCylinder(planarMesh);

  const faceNormalsMesh = new Mesh(cylinder, lineMeshMaterial);
  faceNormalsMesh.name = DEBUG_FACE_NORMALS_NAME;
  faceNormalsMesh.renderOrder = RENDER_ORDER_DEBUG_LINE;

  planarMesh.add(faceNormalsMesh);
}

const initiallySelectedDebugCallbacks: { [key in keyof DebugCallbackType]?: boolean } = {};
const debugCallbackTypes: DebugCallbackType[] = [];
for (const key of Object.keys(DebugCallbackType)) {
  if (isNaN(Number(key))) {
    debugCallbackTypes.push(DebugCallbackType[key]);
    initiallySelectedDebugCallbacks[DebugCallbackType[key]] = false;
  }
}

const DebugCollapsibleSection = (
  props: React.PropsWithChildren<Omit<CollapsibleSectionHeaderProps, "expanded" | "setExpanded">>,
) => {
  const [expanded, setExpanded] = useState(false);

  const header = (
    <CollapsibleSectionHeader
      header={props.header}
      expanded={expanded}
      setExpanded={(val) => setExpanded(val)}
      topBorder={props.topBorder}
    />
  );

  const display = expanded ? "block" : "none";
  const body = <div style={{ display, padding: "8px" }}>{props.children}</div>;
  return (
    <div style={props.style}>
      {header}
      {body}
    </div>
  );
};

type DebugContextProps = {
  faceVerticesEnabled?: boolean;
  faceNormalsEnabled?: boolean;
};
const DebugContext = createContext<DebugContextProps>({});
DebugContext.displayName = "DebugContext";

type DebugInfoProps = {
  debugInfo: any[];
  enabled: boolean;
  viewerRef: React.MutableRefObject<THREEViewerLite | null>;
  className?: string;
};

const DebugInfo = (props: DebugInfoProps) => {
  const debugContext = useContext<DebugContextProps>(DebugContext);

  const [infoByTypes, setInfoByTypes] = useState({});
  const [activeFaceItems, setActiveFaceItems] = useState({});
  const [activeLineItems, setActiveLineItems] = useState({});
  const [activeMeshItems, setActiveMeshItems] = useState({});

  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const [highlightedItem, setHighlightedItem] = useState<string | null>("");

  const viewer = props.viewerRef.current;
  const debugInfo = props.debugInfo;

  useEffect(() => {
    if (!props.enabled || debugInfo.length === 0) {
      reset();
      return;
    }

    const newInfoByTypes: any[] = [];
    debugInfo.forEach((info) => {
      const [, category] = info.id.split("-");

      if (!(info.type in newInfoByTypes)) {
        newInfoByTypes[info.type] = category ? {} : [];
      }

      if (category) {
        if (!(category in newInfoByTypes[info.type])) {
          newInfoByTypes[info.type][category] = [];
        }
        newInfoByTypes[info.type][category].push(info);
      } else {
        newInfoByTypes[info.type].push(info);
      }
    });
    setInfoByTypes(newInfoByTypes);
  }, [debugInfo, props.enabled]);

  const reset = () => {
    setInfoByTypes([]);
    setHighlightedIndex(null);
    setHighlightedItem("");
  };

  const toggleFaceInViewer = (faceId: string, face: Face, isMerged: boolean, isScaledDown: boolean) => {
    const scene = viewer?.scene.scene.getObjectByName(DEBUG_INFO_SCENE_NAME);
    if (!scene || !viewer) {
      return;
    }
    const debugFaceId = `${DEBUG_FACE_PREFIX_NAME}-${faceId}`;

    let faceMesh = scene.getObjectByName(debugFaceId) as Mesh;
    if (!faceMesh) {
      let material: MeshBasicMaterial;
      if (isMerged) {
        material = mergedFaceMaterial;
      } else if (debugFaceId.includes("concave")) {
        material = concaveFaceMaterial;
      } else if (debugFaceId.includes("convex")) {
        material = convexFaceMaterial;
      } else if (debugFaceId.includes("rectangular")) {
        material = rectangularFaceMaterial;
      } else {
        material = faceMaterial;
      }

      if (isScaledDown) {
        face.loops[0] = polyLine2NonUniformScaleAndKeepOrigin(face.loops[0], 0.95, 0.95);
      }

      faceMesh = facesToTHREE([face], material);
      faceMesh.name = debugFaceId;
      faceMesh.renderOrder = isMerged ? RENDER_ORDER_DEBUG_MERGED : RENDER_ORDER_DEBUG_FACE;

      if (debugContext.faceVerticesEnabled) {
        addFaceVertices(faceMesh);
      }

      if (debugContext.faceNormalsEnabled) {
        addFaceNormals(faceMesh);
      }

      scene.add(faceMesh);
    } else {
      faceMesh.visible = !faceMesh.visible;
    }

    viewer.scene.markRenderingNeedsUpdate();
  };

  const toggleMeshInViewer = (meshId: string, mesh: Mesh3) => {
    const scene = viewer?.scene.scene.getObjectByName(DEBUG_INFO_SCENE_NAME);
    if (!scene || !viewer) {
      return;
    }

    let debugMesh = scene.getObjectByName(meshId) as Mesh;
    if (!debugMesh) {
      let material: MeshBasicMaterial;
      if (meshId.includes("outside")) {
        material = outsideMeshMaterial;
      } else if (meshId.includes("original")) {
        material = originalMeshMaterial;
      } else if (meshId.includes("convex")) {
        material = convexMeshMaterial;
      } else if (meshId.includes("concave")) {
        material = concaveMeshMaterial;
      } else {
        material = meshMaterial;
      }
      debugMesh = mesh3ToTHREE(mesh, material);

      const wireframeGeometry = new WireframeGeometry(debugMesh.geometry);
      const wireframeMaterial = new LineBasicMaterial({ color: 0xff0000 });
      const wireframe = new LineSegments(wireframeGeometry, wireframeMaterial);
      debugMesh.add(wireframe);

      debugMesh.name = meshId;
      debugMesh.renderOrder = RENDER_ORDER_DEBUG_FACE;
      scene.add(debugMesh);
    } else {
      debugMesh.visible = !debugMesh.visible;
    }

    viewer.scene.markRenderingNeedsUpdate();
  };

  // Line rendering is tricky in WebGL, as it only supports 1px wide GL_LINES.
  //
  // We could use a library for it too (https://github.com/spite/THREE.MeshLine).
  //
  // In the meantime, representing the line as a cylinder (only supporting two-point PolyLine3 at the moment).
  const toggleLineInViewer = (lineId: string, line: PolyLine3) => {
    const scene = viewer?.scene.scene.getObjectByName(DEBUG_INFO_SCENE_NAME);
    if (!scene) {
      return;
    }

    let debugMesh = scene.getObjectByName(lineId) as Mesh;
    if (!debugMesh) {
      const cylinder = polyLine3ToCylinder(line, 2);

      debugMesh = new Mesh(cylinder, lineMeshMaterial);
      debugMesh.name = lineId;
      debugMesh.renderOrder = RENDER_ORDER_DEBUG_LINE;
      scene.add(debugMesh);
    } else {
      debugMesh.visible = !debugMesh.visible;
    }

    viewer?.scene.markRenderingNeedsUpdate();
  };

  const resetDebugFaces = () => {
    const scene = viewer?.scene.scene.getObjectByName(DEBUG_INFO_SCENE_NAME);
    if (!scene) {
      return;
    }

    for (const debugElement of scene.children) {
      (debugElement as Mesh).visible = false;
    }

    setActiveFaceItems({});
    setActiveLineItems({});
    setActiveMeshItems({});

    viewer?.scene.markRenderingNeedsUpdate();
  };

  const filterDebugInfo = (event) => {
    if (event.key === "Enter") {
      const element = event.target;

      let regex: RegExp;

      try {
        regex = new RegExp(element.value, "i");
      } catch {
        return;
      }

      resetDebugFaces();
      const debugItems = element.parentNode.querySelectorAll(":scope .debugItem");

      let filteredCount = 0;
      debugItems.forEach((debugItem: HTMLDivElement) => {
        if (regex.test(debugItem.innerHTML)) {
          filteredCount += 1;
          debugItem.classList.add("filtered");
          debugItem.classList.remove("hidden");
        } else {
          debugItem.classList.remove("filtered");
          debugItem.classList.add("hidden");
        }
      });

      const reset = element.value.trim() === "" || filteredCount === 0;
      if (reset) {
        const hiddenItems = element.parentNode.querySelectorAll(":scope .debugItem.hidden");
        hiddenItems.forEach((debugItem: HTMLDivElement) => {
          debugItem.classList.remove("hidden");
        });
      }
      setHighlightedIndex(reset ? null : -1);
      setHighlightedItem(reset ? "" : `${filteredCount} results`);
    }
  };

  const highlightFiltered = (event, selectionType: "next" | "all") => {
    const element = event.target;
    const debugItems = element.parentNode.querySelectorAll(":scope .debugItem.filtered");

    // Untoggle last item
    if (highlightedIndex !== -1 && highlightedIndex !== null) {
      const currentDebugItem = debugItems[highlightedIndex];
      currentDebugItem.click();
    }

    if (selectionType === "all") {
      debugItems.forEach((debugItem: HTMLDivElement) => debugItem.click());
      setHighlightedIndex(-1);
      setHighlightedItem(null);
    } else {
      if (highlightedIndex !== null) {
        // Toggle next item
        const nextHighlightIndex = (highlightedIndex + 1) % debugItems.length;
        if (debugItems.length > 0) {
          const nextDebugItem = debugItems[nextHighlightIndex];
          nextDebugItem.click();
        }

        setHighlightedIndex(nextHighlightIndex);
        setHighlightedItem(debugItems[nextHighlightIndex].innerHTML);
      }
    }
  };

  const FaceItem = (faceId: string, face: Face, isMerged: boolean = false, isScaledDown: boolean = false) => {
    const enabledBorder = isMerged ? "green" : "red";
    const enabledBackground = isMerged ? "lightgreen" : "pink";

    const enabled = activeFaceItems[faceId];
    const setEnabled = (v: boolean) => setActiveFaceItems({ ...activeFaceItems, [faceId]: v });

    const style = {
      borderWidth: "1px",
      borderStyle: "solid",
      borderRadius: "8px",
      padding: "2px",
      borderColor: enabled ? enabledBorder : "gray",
      backgroundColor: enabled ? enabledBackground : "transparent",
    };

    return (
      <div
        className="debugItem"
        key={faceId}
        style={style}
        onClick={() => {
          toggleFaceInViewer(faceId, face, isMerged, isScaledDown);
          setEnabled(!enabled);
        }}
      >
        {faceId}
      </div>
    );
  };
  const LineItem = (lineId: string, line: PolyLine3) => {
    const enabled = activeLineItems[lineId];
    const setEnabled = (v: boolean) => setActiveLineItems({ ...activeLineItems, [lineId]: v });

    const style = {
      borderWidth: "1px",
      borderStyle: "solid",
      borderRadius: "8px",
      padding: "2px",
      borderColor: enabled ? "purple" : "gray",
      backgroundColor: enabled ? "lavender" : "transparent",
    };

    return (
      <div
        className="debugItem"
        key={lineId}
        style={style}
        onClick={() => {
          toggleLineInViewer(lineId, line);
          setEnabled(!enabled);
        }}
      >
        {lineId}
      </div>
    );
  };

  const MeshItem = (meshId: string, mesh: Mesh3) => {
    const enabled = activeMeshItems[meshId];
    const setEnabled = (v: boolean) => setActiveMeshItems({ ...activeMeshItems, [meshId]: v });

    const style = {
      borderWidth: "1px",
      borderStyle: "solid",
      borderRadius: "8px",
      padding: "2px",
      borderColor: enabled ? "purple" : "gray",
      backgroundColor: enabled ? "lavender" : "transparent",
    };

    return (
      <div
        className="debugItem"
        key={meshId}
        style={style}
        onClick={() => {
          toggleMeshInViewer(meshId, mesh);
          setEnabled(!enabled);
        }}
      >
        {meshId}
      </div>
    );
  };

  const faceMerge = (type: DebugCallbackType) => (
    <>
      {infoByTypes[type] &&
        Object.keys(infoByTypes[type]).map((category) => (
          <DebugCollapsibleSection key={category} header={category}>
            {infoByTypes[type][category].map((info) => (
              <DebugCollapsibleSection key={info.id} header={info.id}>
                {info.in.map((face, index) => FaceItem(`${info.id}-in${index}`, face))}
                {info.out.map((face, index) => FaceItem(`${info.id}-merged${index}`, face, true))}
              </DebugCollapsibleSection>
            ))}
          </DebugCollapsibleSection>
        ))}
    </>
  );

  const faceConflictDebugInfo = () => {
    const conflicts = infoByTypes[DebugCallbackType.DETECT_MATERIAL_OVERLAP];
    return (
      <>
        {conflicts?.map((info) => (
          <DebugCollapsibleSection key={info.id} header={info.id}>
            {info.in.map((face, index) => FaceItem(`${info.id}-in${index}`, face))}
            {info.out.map((face, index) => FaceItem(`${info.id}-conflict${index}`, face, true))}
          </DebugCollapsibleSection>
        ))}
      </>
    );
  };

  const faceDebugInfo = () => {
    const facesInfo = infoByTypes[DebugCallbackType.FACE_DEBUG];
    return (
      <>
        {facesInfo?.map((info) => (
          <DebugCollapsibleSection key={info.id} header={info.id}>
            {info.faces.map((face, index) => FaceItem(`${info.id}-face${index}`, face))}
            {info.lines.map((line, index) => LineItem(`${info.id}-line${index}`, line))}
            {info.meshes.map((mesh, index) => MeshItem(`${info.id}-mesh${index}`, mesh))}
          </DebugCollapsibleSection>
        ))}
      </>
    );
  };

  const faceMiterDebugInfo = () => {
    const facesInfo = infoByTypes[DebugCallbackType.FACE_MITER];

    return (
      <>
        {facesInfo?.map((info) => (
          <DebugCollapsibleSection key={info.id} header={info.id}>
            {info.inputFaces.map((face, index) => FaceItem(`${info.id}-in${index}`, face))}
            {info.miterLines.map((line, index) => LineItem(`${info.id}-miterLine${index}`, line))}
            {info.miterFaces.map((face, index) => FaceItem(`${info.id}-miterPlane${index}`, face, true))}
          </DebugCollapsibleSection>
        ))}
      </>
    );
  };

  const materialDebugInfo = () => {
    const type = DebugCallbackType.GENERATE_MATERIAL_WITHIN_FACES;

    const filterCells = (element) => {
      resetDebugFaces();

      let regex: RegExp;

      try {
        regex = new RegExp(element.value);
      } catch {
        return;
      }

      const debugItems = element.parentNode.querySelectorAll(":scope > div > div .debugItem");

      debugItems.forEach((debugItem: HTMLDivElement) => {
        if (regex.test(debugItem.innerHTML)) {
          debugItem.click();
        }
      });
    };

    return (
      <>
        {infoByTypes[type]?.map((info) => {
          const patterns = Object.keys(info.inputCells);
          return (
            <DebugCollapsibleSection key={info.id} header={info.id}>
              {FaceItem(`${info.id}-generationSupport`, info.in, true)}
              <hr />
              <input
                type="text"
                placeholder="regex"
                onKeyPress={(event) => {
                  if (event.key === "Enter") {
                    filterCells(event.target);
                  }
                }}
              />
              {patterns.map((pattern) => {
                const key = `${info.id}-l${pattern}`;

                const inputCell = info.inputCells[pattern];
                const outputCells = info.outputCells[pattern];
                const patternSpans = info.patternSpans[pattern];
                const patternLoops = info.patternLoops[pattern];
                const patternGeometries = info.patternGeometries[pattern];

                return (
                  <DebugCollapsibleSection key={key} header={key}>
                    {Object.keys(inputCell).map((cellKey) =>
                      FaceItem(`${key}-${cellKey}-inputCell`, inputCell[cellKey], false, true),
                    )}
                    {Object.keys(outputCells).map((cellKey) =>
                      FaceItem(`${key}-${cellKey}-outputCell`, outputCells[cellKey], true, false),
                    )}
                    {Object.keys(patternSpans).map((patternKey) =>
                      FaceItem(`${key}-${patternKey}`, patternSpans[patternKey], true, false),
                    )}
                    {Object.keys(patternGeometries).map((patternKey) =>
                      MeshItem(`${key}-${patternKey}`, patternGeometries[patternKey]),
                    )}
                    {Object.keys(patternLoops).map((patternKey) =>
                      FaceItem(`${key}-${patternKey}`, patternLoops[patternKey]),
                    )}
                  </DebugCollapsibleSection>
                );
              })}
            </DebugCollapsibleSection>
          );
        })}
      </>
    );
  };

  const simplifyFaceDebugInfo = () => {
    const facesInfo = infoByTypes[DebugCallbackType.SIMPLIFY_FACES];

    return (
      <>
        {facesInfo &&
          Object.keys(facesInfo).map((category) => (
            <DebugCollapsibleSection key={category} header={category}>
              {facesInfo[category].map((info) => (
                <DebugCollapsibleSection key={info.id} header={info.id}>
                  {FaceItem(`${info.id}-in`, info.in)}
                  {FaceItem(`${info.id}-out`, info.out, true)}
                </DebugCollapsibleSection>
              ))}
            </DebugCollapsibleSection>
          ))}
      </>
    );
  };

  const debugInfoSection = {
    [DebugCallbackType.DETECT_MATERIAL_OVERLAP]: faceConflictDebugInfo(),
    [DebugCallbackType.FACE_DEBUG]: faceDebugInfo(),
    [DebugCallbackType.FACE_MITER]: faceMiterDebugInfo(),
    [DebugCallbackType.GENERATE_MATERIAL_WITHIN_FACES]: materialDebugInfo(),
    [DebugCallbackType.MERGE_FACES_OF_SAME_CATEGORY]: faceMerge(DebugCallbackType.MERGE_FACES_OF_SAME_CATEGORY),
    [DebugCallbackType.MERGE_FACES_OF_SAME_MATERIAL]: faceMerge(DebugCallbackType.MERGE_FACES_OF_SAME_MATERIAL),
    [DebugCallbackType.SIMPLIFY_FACES]: simplifyFaceDebugInfo(),
  };

  return (
    <div className={props.className}>
      {Object.keys(infoByTypes).length > 0 && (
        <>
          Filter:
          <input type="text" onKeyDown={filterDebugInfo} />
          {highlightedIndex !== null && <button onClick={(event) => highlightFiltered(event, "next")}>Next</button>}
          {highlightedIndex !== null && <button onClick={(event) => highlightFiltered(event, "all")}>All</button>}
          {highlightedItem !== "" && <div>{highlightedItem}</div>}
          {Object.keys(debugInfoSection).map((key) => (
            <div key={key} style={{ display: infoByTypes[key] ? "block" : "none" }}>
              <DebugCollapsibleSection header={DebugCallbackType[key]}>{debugInfoSection[key]}</DebugCollapsibleSection>
              <div className="debug-reset">
                <a onClick={resetDebugFaces}>Reset</a>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

type ToggleItemProps = {
  name: string;
  onChange: () => void;
  enabled: boolean | undefined | null;
  color?: string;
  style?: React.CSSProperties;
};

const ToggleItem = (props: ToggleItemProps) => {
  const enabled = Boolean(props.enabled);

  const disabledColor = "black";
  const enabledColor = props.color ?? disabledColor;

  const itemStyle: React.CSSProperties = {
    whiteSpace: "nowrap",
    color: enabled ? enabledColor : disabledColor,
    fontWeight: props.color && enabled ? "bold" : "normal",
    ...props.style,
  };

  const inputId = toConstantCase(props.name);

  return (
    <div style={itemStyle}>
      <input type="checkbox" onChange={() => props.onChange()} checked={enabled} id={inputId} /> &nbsp;
      <label htmlFor={inputId}>{props.name}</label>
    </div>
  );
};

type DropdownMenuProps = {
  title: string;
  canToggleAll?: boolean;
  children?: ReactNode;
  className?: string;
};

const DropdownMenu = (props: DropdownMenuProps) => {
  const [active, setActive] = useState(false);
  const [globalToggle, setGlobalToggle] = useState<"true" | "false" | "mixed">("true");
  const [staleCheckboxes, setStaleCheckboxes] = useState<HTMLInputElement[]>([]);

  const dropDownRef = useRef<HTMLDivElement>(null);

  const dropdownMenuYStart = dropDownRef.current?.getBoundingClientRect().bottom ?? 28;

  const dropdownStyle: React.CSSProperties = {
    position: "absolute",
    top: `${dropdownMenuYStart}px`,
    zIndex: 10001, // on top of dat.gui
    display: active ? "inline-block" : "none",
    backgroundColor: "white",
    padding: "2px",
  };

  if (props.className?.includes("right")) {
    dropdownStyle.right = "0px";
  } else {
    dropdownStyle.left = "0px";
  }

  const updateGlobalToggle = () => {
    const nextState = globalToggle === "true" || globalToggle === "mixed" ? "false" : "true";
    if (!dropDownRef.current) {
      return;
    }

    const checkboxes = dropDownRef.current.querySelectorAll(":scope > div + div input[type=checkbox]");
    const checkboxesToClick: HTMLInputElement[] = [];
    checkboxes.forEach((el: Element) => {
      const checkbox = el as HTMLInputElement;
      const checkboxValue = checkbox.checked.toString();
      if (checkboxValue !== nextState) {
        checkboxesToClick.push(checkbox);
      }
    });

    setStaleCheckboxes(checkboxesToClick);
    setGlobalToggle(nextState);
  };

  useEffect(() => {
    if (!props.canToggleAll || !dropDownRef.current) {
      return;
    }

    const checkboxes = dropDownRef.current.querySelectorAll(":scope > div + div input[type=checkbox]");
    let ariaChecked: string | undefined = undefined;
    checkboxes.forEach((el: Element) => {
      const checkbox = el as HTMLInputElement;
      const checkboxValue = checkbox.checked.toString();
      if (!ariaChecked) {
        ariaChecked = checkboxValue;
      } else {
        if (ariaChecked !== checkboxValue) {
          ariaChecked = "mixed";
        }
      }
    });

    const globalCheckbox = dropDownRef.current.querySelector(
      ":scope > div:first-child input[type=checkbox]",
    ) as HTMLInputElement;
    globalCheckbox.indeterminate = ariaChecked === "mixed";
    globalCheckbox.checked = ariaChecked === "true";
    if (ariaChecked === "mixed" || ariaChecked === "true" || ariaChecked === "false") {
      setGlobalToggle(ariaChecked);
    }
  }, [dropDownRef, props.children]);

  useEffect(() => {
    if (!props.canToggleAll || !dropDownRef.current) {
      return;
    }

    if (staleCheckboxes.length === 0) {
      return;
    } else {
      const checkbox: HTMLInputElement = staleCheckboxes[0];
      checkbox.click();
      setStaleCheckboxes(staleCheckboxes.slice(1));
    }
  }, [staleCheckboxes]);

  return (
    <div ref={dropDownRef} className={styles.dropdownMenu + " " + (props.className ?? "")}>
      <div onClick={() => setActive(!active)}>
        {props.canToggleAll && (
          <input
            type="checkbox"
            aria-checked={globalToggle}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onChange={() => updateGlobalToggle()}
          />
        )}
        &nbsp;
        <span>{props.title} ▾</span>
      </div>
      <div style={dropdownStyle}>{props.children}</div>
    </div>
  );
};

function DebugExportPage() {
  const [state, dispatch] = useReducer(exportDebuggerReducer, {
    exportOptions: {
      ...defaultExportOptions(),
      mergeCoplanarMaterialFaces: false, // do not use defaults on launch
    },
  });

  const { setUserNotification } = useAppContext();
  const buildingViewerContainerRef = useRef<HTMLDivElement>(null);
  const exportViewerContainerRef = useRef<HTMLDivElement>(null);
  const exportViewerRef = useRef<THREEViewerLite | null>(null);

  const buildingViewerSystemRef = useRef<THREEViewerSystem>(null);

  // Debugger info
  const [exportLoadingState, setExportLoadingState] = useState<LoadState | null>(null);
  const [hasExport, setHasExport] = useState<boolean>(false);
  const [selectedDebugCallbacks, setSelectedDebugCallbacks] = useState<object>(initiallySelectedDebugCallbacks);
  const [debugInfo, setDebugInfo] = useState<any[]>([]);
  // Debugger state
  const [wireframeEnabled, setWireframeEnabled] = useState<boolean | null>(null);
  const [groundplaneEnabled, setGroundplaneEnabled] = useState<boolean>(true);
  const [vertexNormalsEnabled, setVertexNormalsEnabled] = useState<boolean | null>(null);
  const [debugModeMap, setDebugModeMap] = useState<string>("");
  const [debugUvScale, setDebugUvScale] = useState<number>(1);
  const [debugFaceVerticesEnabled, setDebugFaceVerticesEnabled] = useState<boolean>(false);
  const [debugFaceNormalsEnabled, setDebugFaceNormalsEnabled] = useState<boolean>(false);
  const [debugInfoEnabled, setDebugInfoEnabled] = useState<boolean>(false);
  // Materials
  const [geometryGenerationDebugIdFilter, setGeometryGenerationDebugIdFilter] = useState<string>("");
  const [geometryGenerationFaceCategoryFilter, setGeometryGenerationFaceCategoryFilter] = useState<string>("");

  // Camera
  const [orbitSpeed, setOrbitSpeed] = useState<number>(1.0);
  const [dollySpeed, setDollySpeed] = useState<number>(1.0);
  const [panSpeed, setPanSpeed] = useState<number>(1.0);
  const [nearClipDistance, setNearClipDistance] = useState<number>(1);
  const [farClipDistance, setFarClipDistance] = useState<number>(10000);

  const assetLoader = THREEAssetLoader.instance;

  // Source data
  const { loading, building, viewFilter } = useCustomizerSelector((cus) => {
    const { loading, building } = cus;
    return {
      building,
      loading,
      viewFilter: cus.sceneState.viewFilter ?? defaultExporterView3DFilter,
    };
  });
  const buildingDispatch = useCustomizerDispatch();

  const [configuration, setConfiguration] = useState<ExportConfiguration>();

  // Helpers
  const requestRender = () => {
    exportViewerRef.current?.scene.markRenderingNeedsUpdate();
  };

  const getScene = (name = GLTF_SCENE_NAME, throwWhenMissing: boolean = true): Scene => {
    const scene = exportViewerRef.current?.scene.scene.getObjectByName(name);
    if (!scene && throwWhenMissing) {
      throw `Cannot find export scene ${name}`;
    }
    return scene as Scene;
  };

  const clearExportScene = () => {
    const viewer = exportViewerRef.current;

    for (const name of [GLTF_SCENE_NAME, DEBUG_INFO_SCENE_NAME]) {
      const oldScene = getScene(name, false);
      if (oldScene && viewer) {
        viewer.scene.remove(oldScene);
      }
    }

    setHasExport(false);
    setDebugInfo([]);
    setDebugInfoEnabled(false);

    requestRender();
  };

  const isMesh = (o: Object3D): o is Mesh<BufferGeometry> => {
    return o instanceof Mesh && o.isMesh;
  };

  const isMeshWithPosition = (o: Object3D): o is Mesh<BufferGeometry> => {
    return isMesh(o) && o.geometry !== undefined && o.geometry.attributes.position !== undefined;
  };

  const isMeshWithNormal = (o: Object3D): o is Mesh<BufferGeometry> => {
    return isMesh(o) && o.geometry !== undefined && o.geometry.attributes.normal !== undefined;
  };

  // Actions
  const openBuilding = async () => {
    const fileDialog = document.createElement("input");
    fileDialog.type = "file";

    fileDialog.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement;

      if (target.files?.length === 0) {
        return;
      }

      clearExportScene();

      const reader = new FileReader();
      reader.onload = async (e) => {
        const docJson = e.target?.result;
        if (typeof docJson !== "string") {
          return;
        }
        try {
          const [{ docParse }, { getDocWithOverrides }, { generateBuilding }] = await Promise.all([
            import("doc/docMarshal"),
            import("doc/options/mergeOptionTemplatesIntoOptionSets"),
            import("building-generation"),
          ]);
          const doc = getDocWithOverrides(docParse(docJson));
          const building = await generateBuilding({
            name: doc.name,
            optionChoices: doc.optionChoices,
            doc: doc,
          });

          buildingDispatch(setLoading(LoadState.FULLSCREEN));
          buildingDispatch(setBuilding(building));
          setConfiguration(undefined);
          buildingDispatch(setViewMode(BuildingViewerViewMode.EXTERIOR));
        } catch (e) {
          const error: UserNotificationType = isUserNotification(e)
            ? e
            : {
                message: "An error occured while opening building",
                messageType: "error",
              };
          setUserNotification(error);
        }
      };

      if (target.files) {
        reader.readAsText(target.files[0]);
      }
    };

    fileDialog.click();
  };

  const openConfiguration = async () => {
    const fileDialog = document.createElement("input");
    fileDialog.type = "file";

    fileDialog.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement;

      if ((target.files?.length ?? 0) === 0) {
        return;
      }

      clearExportScene();

      const reader = new FileReader();
      reader.onload = async (e) => {
        if (!e.target) {
          return;
        }

        const configurationJson = e.target.result as string;
        try {
          const configuration = configurationParse(configurationJson);
          setConfiguration(configuration);
          buildingDispatch(setBuilding(null));
        } catch (e) {
          const error: UserNotificationType = {
            message: "An error occured while opening configuration",
            messageType: "error",
          };
          setUserNotification(error);
        }
      };

      if (target.files) {
        reader.readAsText(target.files[0]);
      }
    };

    fileDialog.click();
  };

  // TODO: This seems like it's the wrong way around. Redefine this in terms of BuildingGeometry types?
  function buildingElementMaskFromRenderStageFilter(renderStageFilter: ViewFilter): Set<FaceCategory> {
    // Determine active render stages
    const stages = Object.keys(renderStageFilter).filter(
      (k) => renderStageFilter[k] && !renderStageConstants.hasOwnProperty(k),
    ) as (keyof ViewFilter)[];

    // Currently unmatched building elements:
    //
    // BuildingElementMask.Glass
    // BuildingElementMask.Meshes
    // BuildingElementMask.Screens

    const buildMask = new Set<FaceCategory>();
    for (const stage of stages) {
      switch (stage) {
        case "casework":
          buildMask.add(FaceCategory.CASEWORK);
          break;
        case "ceilings":
          buildMask.add(FaceCategory.CEILING);
          break;
        case "doorUnits":
          buildMask.add(FaceCategory.DOOR_EXTERIOR);
          buildMask.add(FaceCategory.DOOR_INTERIOR);
          buildMask.add(FaceCategory.DOOR_DECORATION);
          buildMask.add(FaceCategory.DOOR_CURVED_WALL_EXTERIOR);
          buildMask.add(FaceCategory.DOOR_CURVED_WALL_INTERIOR);
          break;
        case "cornerBoards":
          buildMask.add(FaceCategory.CORNER_BOARD);
          break;
        case "flooring":
          buildMask.add(FaceCategory.FLOOR);
          break;
        case "floorFraming":
          buildMask.add(FaceCategory.FLOOR_FRAMING);
          break;
        case "subFlooring":
          buildMask.add(FaceCategory.SUB_FLOORING);
          break;
        case "slabFlooring":
          buildMask.add(FaceCategory.SLAB_FLOORING);
          break;
        case "slab":
          buildMask.add(FaceCategory.FOUNDATION_SLAB);
          break;
        case "stemWall":
          buildMask.add(FaceCategory.FOUNDATION_STEM_WALL);
          break;
        case "foundationCurb":
          buildMask.add(FaceCategory.FOUNDATION_GARAGE_CURB);
          break;
        case "foundationFooting":
          buildMask.add(FaceCategory.FOUNDATION_FOOTING);
          break;
        case "foundationPiers":
          buildMask.add(FaceCategory.FOUNDATION_PIER);
          break;
        case "porches":
          buildMask.add(FaceCategory.PORCH_EXTERIOR);
          break;
        case "propAppliances":
          buildMask.add(FaceCategory.PROP_APPLIANCES);
          break;
        case "propFixtures":
          buildMask.add(FaceCategory.PROP_FIXTURES);
          break;
        case "propFurniture":
          buildMask.add(FaceCategory.PROP_FURNITURE);
          break;
        case "propOther":
          buildMask.add(FaceCategory.PROP_OTHER);
          break;
        case "propFoliage":
          buildMask.add(FaceCategory.PROP_FOLIAGE);
          break;
        case "railings":
          buildMask.add(FaceCategory.RAILING);
          break;
        case "roofs":
          buildMask.add(FaceCategory.ROOF_TOP);
          buildMask.add(FaceCategory.ROOF_BOTTOM);
          buildMask.add(FaceCategory.ROOF_RIDGE_CAP);
          for (const faceCategory of roofTrimFaceCategories) {
            buildMask.add(faceCategory);
          }
          break;
        case "roofRidgeVent":
          buildMask.add(FaceCategory.ROOF_RIDGE_VENT);
          break;
        case "roofing":
          buildMask.add(FaceCategory.ROOF_TOP_CUSTOMIZABLE_EXPORT_GEOMETRY);
          break;
        case "roofFraming":
          buildMask.add(FaceCategory.ROOF_FRAMING);
          break;
        case "site":
          buildMask.add(FaceCategory.GROUND);
          break;
        case "stairSystems":
          buildMask.add(FaceCategory.STAIR);
          break;
        case "wainscoting":
          buildMask.add(FaceCategory.WAINSCOTING);
          buildMask.add(FaceCategory.WAINSCOTING_TRIM);
          break;
        case "wallFraming":
          buildMask.add(FaceCategory.WALL_FRAMING);
          break;
        case "gableEndWallFraming":
          buildMask.add(FaceCategory.GABLE_END_WALL_FRAMING);
          break;
        case "walls":
          for (const faceCategory of wallFaceCategories) {
            buildMask.add(faceCategory);
          }
          break;
        case "windowUnits":
          buildMask.add(FaceCategory.WINDOW_EXTERIOR);
          buildMask.add(FaceCategory.WINDOW_INTERIOR);
          buildMask.add(FaceCategory.WINDOW_GRID);
          buildMask.add(FaceCategory.WINDOW_DECORATION);
          buildMask.add(FaceCategory.SKYLIGHT_DECORATION);
          buildMask.add(FaceCategory.SKYLIGHT_EXTERIOR);

          // TODO: Is glass wanted here?
          buildMask.add(FaceCategory.WINDOW_GLASS);
          buildMask.add(FaceCategory.WINDOW_SCREEN);
          buildMask.add(FaceCategory.SKYLIGHT_GLASS);
          break;
        case "windowShutters":
          break;
        case "baseTrimMolding":
          buildMask.add(FaceCategory.BASE_TRIM_MOLDING);
          break;
        case "crownTrimMolding":
          buildMask.add(FaceCategory.CROWN_TRIM_MOLDING);
          break;
        case "columns":
          buildMask.add(FaceCategory.COLUMN);
          break;
        case "driveways":
          console.warn("Unsupported render stage", stage);
          break;
        default:
          break;
      }
    }
    return buildMask;
  }

  const buildExportOptions = (): ExportOptions => {
    const exportMask = buildingElementMaskFromRenderStageFilter({ ...emptyViewFilter, ...viewFilter });

    const exportOptions: ExportOptions = {
      ...state.exportOptions,
      exportMask,
    };

    if (geometryGenerationDebugIdFilter !== "") {
      const debugIds = geometryGenerationDebugIdFilter
        .split(",")
        .map((id) => parseInt(id))
        .filter((id) => !Number.isNaN(id));

      if (debugIds.length > 0) {
        exportOptions.geometryGenerationDebugIdFilter = debugIds;
      }
    }

    if (geometryGenerationFaceCategoryFilter !== "") {
      const faceCategories = geometryGenerationFaceCategoryFilter
        .split(",")
        .map((name) => FaceCategory[name])
        .filter((faceCategory) => !!faceCategory);

      if (faceCategories.length > 0) {
        exportOptions.geometryGenerationFaceCategoryFilter = faceCategories;
      }
    }

    return exportOptions;
  };

  const downloadBuilding = async () => {
    const exportOptions = buildExportOptions();
    exportOptions.exportName = "Download";

    if (!building) {
      return;
    }

    const buildingName = building.name.trim();
    const [{ optionSetSelectedOptionId }, { SystemOptionSet }] = await Promise.all([
      import("doc/options/optionSetTools"),
      import("doc/options/SystemOptionSetEnum"),
    ]);
    const elevationName = optionSetSelectedOptionId(
      building.doc.optionSets,
      building.optionChoices,
      SystemOptionSet.ELEVATION_STYLE,
    )?.trim();
    const filename = `${buildingName}-${elevationName}-${Date.now()}`;

    await downloadExportGLTF(building, filename, exportOptions);
  };

  const exportBuildingGLTFIntoView = async () => {
    const viewer = exportViewerRef.current;

    if (!viewer || !building) {
      return;
    }

    clearExportScene();
    setExportLoadingState(LoadState.FULLSCREEN);

    const exportOptions = buildExportOptions();
    exportOptions.exportName = "Export to glTF";
    const info: any[] = [];
    const debugCallback = (o: any) => {
      info.push(o);
    };
    exportOptions.debugCallbacks = {};
    for (const debugCallbackType of debugCallbackTypes) {
      if (selectedDebugCallbacks[debugCallbackType]) {
        exportOptions.debugCallbacks[debugCallbackType] = debugCallback;
      }
    }

    let buildingAsGltf: ArrayBuffer | undefined;
    if (configuration) {
      console.warn("glTF visualization of configuration is not currently supported");
    } else {
      buildingAsGltf = await exportBuildingGLTF(building, assetLoader, exportOptions);
    }

    if (!buildingAsGltf) {
      return;
    }

    const gltf = await assetLoader.gltfLoader.parseAsync(buildingAsGltf, "");
    const gltfScene = gltf.scene;
    gltfScene.name = GLTF_SCENE_NAME;

    viewer.scene.add(gltfScene);
    const infoScene = new Scene();
    infoScene.name = DEBUG_INFO_SCENE_NAME;
    viewer.scene.add(infoScene);

    setHasExport(true);
    setDebugInfo(info);
    setDebugInfoEnabled(true);
    requestRender();
    setExportLoadingState(null);
  };

  // Toggling

  const toggleWireframe = (reset: boolean = false) => {
    const scene = getScene();

    if (wireframeEnabled === null || reset) {
      scene.traverse((o: Object3D) => {
        if (isMeshWithPosition(o)) {
          const wireframeGeometry = new WireframeGeometry(o.geometry);
          const wireframeMaterial = new LineBasicMaterial({ color: 0x0000ff });
          const wireframe = new LineSegments(wireframeGeometry, wireframeMaterial);

          wireframe.name = WIREFRAME_NAME;
          o.add(wireframe);
        }
      });
      setWireframeEnabled(true);
    } else {
      scene.traverse((o: Object3D) => {
        if (o.name === WIREFRAME_NAME) {
          o.visible = !wireframeEnabled;
        }
      });
      setWireframeEnabled(!wireframeEnabled);
    }

    requestRender();
  };

  const toggleGroundplane = () => {
    const groundplane = getScene("groundplane");
    if (!groundplane) {
      return;
    }

    groundplane.visible = !groundplaneEnabled;
    setGroundplaneEnabled(!groundplaneEnabled);

    requestRender();
  };

  const toggleVertexNormals = () => {
    const scene = getScene();

    if (vertexNormalsEnabled === null) {
      scene.traverse((o: Object3D) => {
        if (isMeshWithNormal(o)) {
          const normals = new VertexNormalsHelper(o, 10, 0xff0000);
          normals.name = VERTEX_NORMALS_NAME;
          o.add(normals);
        }
      });
      setVertexNormalsEnabled(true);
    } else {
      scene.traverse((o: Object3D) => {
        if (o.name === VERTEX_NORMALS_NAME) {
          o.visible = !vertexNormalsEnabled;
        }
      });
      setVertexNormalsEnabled(!vertexNormalsEnabled);
    }
    requestRender();
  };

  const toggleDebugModeMap = (mapType: string) => {
    const scene = getScene();
    const newDebugModeMap = debugModeMap === mapType ? "" : mapType;

    scene.traverse((o: Object3D) => {
      if (isMeshWithPosition(o)) {
        const materials = Array.isArray(o.material) ? o.material : [o.material];
        const originalMaterial = materials.filter(
          (material: Material) =>
            material.name !== WORLD_SPACE_NORMAL_MATERIAL_NAME && material.name !== WORLD_SPACE_UV_MATERIAL_NAME,
        )[0];

        if (newDebugModeMap === "") {
          o.material = originalMaterial;
        } else {
          let worldSpaceMaterial: Material | undefined;
          switch (newDebugModeMap) {
            case "normal":
              worldSpaceMaterial = worldSpaceNormalMaterial;
              break;
            case "uv":
              worldSpaceMaterial = worldSpaceUVMaterial;
              break;
            case "winding":
              worldSpaceMaterial = windingOrderMaterial;
              break;
          }

          if (worldSpaceMaterial) {
            o.material = [worldSpaceMaterial, originalMaterial];
          }
        }
      }
    });

    setDebugModeMap(newDebugModeMap);
    requestRender();
  };

  const toggleDebugFaceVertices = () => {
    const scene = getScene(DEBUG_INFO_SCENE_NAME);

    scene.traverse((o: Object3D) => {
      if (isMesh(o) && o.name.startsWith(DEBUG_FACE_PREFIX_NAME)) {
        const faceVerticesMesh = o.getObjectByName(DEBUG_FACE_VERTICES_NAME) as Mesh;
        if (faceVerticesMesh) {
          faceVerticesMesh.visible = !debugFaceVerticesEnabled;
        } else {
          addFaceVertices(o);
        }
      }
    });

    setDebugFaceVerticesEnabled(!debugFaceVerticesEnabled);
    requestRender();
  };

  const toggleDebugFaceNormals = () => {
    const scene = getScene(DEBUG_INFO_SCENE_NAME);

    scene.traverse((o: Object3D) => {
      if (isMesh(o) && o.name.startsWith(DEBUG_FACE_PREFIX_NAME)) {
        const faceVerticesMesh = o.getObjectByName(DEBUG_FACE_NORMALS_NAME) as Mesh;
        if (faceVerticesMesh) {
          faceVerticesMesh.visible = !debugFaceNormalsEnabled;
        } else {
          addFaceNormals(o);
        }
      }
    });

    setDebugFaceNormalsEnabled(!debugFaceNormalsEnabled);
    requestRender();
  };

  const displayWalls = (wallIds: string) => {
    if (!building) {
      return;
    }

    const scene = getScene(DEBUG_INFO_SCENE_NAME);
    let faceGroup = scene.getObjectByName(DEBUG_WALL_NAME) as Group;
    if (faceGroup) {
      for (let i = faceGroup.children.length - 1; i >= 0; i--) {
        faceGroup.remove(faceGroup.children[i]);
      }
    } else {
      faceGroup = new Group();
      faceGroup.name = DEBUG_WALL_NAME;
      faceGroup.renderOrder = RENDER_ORDER_DEBUG_FACE;
      scene.add(faceGroup);
    }

    const selectedWallIds = wallIds.split(",");
    const { levels, walls } = building;
    for (const levelId in levels) {
      const levelWalls = walls[levelId].walls;
      for (const wallId in levelWalls) {
        if (selectedWallIds.includes(wallId) && levelWalls[wallId].wallType === WallType.SOLID) {
          const wall = levelWalls[wallId] as SolidWall;

          faceGroup.add(facesToTHREE(wall.wallFaces.leftFaces, faceMaterial));
          faceGroup.add(facesToTHREE(wall.wallFaces.rightFaces, faceMaterial));
          faceGroup.add(facesToTHREE(wall.wallFaces.bottomFaces, faceMaterial));
          faceGroup.add(facesToTHREE(wall.wallFaces.topFaces, faceMaterial));
          faceGroup.add(facesToTHREE(wall.wallFaces.startFaces, faceMaterial));
          faceGroup.add(facesToTHREE(wall.wallFaces.endFaces, faceMaterial));
        }
      }
    }

    requestRender();
  };

  const displayPoint = (coords: string) => {
    const scene = getScene(DEBUG_INFO_SCENE_NAME);
    let pointGroup = scene.getObjectByName(DEBUG_POINT_NAME) as Group;
    if (pointGroup) {
      for (let i = pointGroup.children.length - 1; i >= 0; i--) {
        pointGroup.remove(pointGroup.children[i]);
      }
    } else {
      pointGroup = new Group();
      pointGroup.name = DEBUG_POINT_NAME;
      pointGroup.renderOrder = RENDER_ORDER_DEBUG_POINT;
      scene.add(pointGroup);
    }

    const xyz = coords.split(",").map((v) => parseFloat(v));
    if (xyz.length >= 3 && xyz.length % 3 === 0 && xyz.every((v) => !isNaN(v))) {
      for (let i = 0; i < xyz.length / 3; i++) {
        const coords = new Float32Array(xyz.slice(3 * i, 3 * (i + 1)));
        const pointGeometry = new BufferGeometry();
        pointGeometry.setAttribute("position", new BufferAttribute(coords, 3));

        const pointMesh = new Points(pointGeometry, selectedPointsMaterial);
        pointGroup.add(pointMesh);
      }
    }

    requestRender();
  };

  const toggleSeparator = (event) => {
    const separator = event.currentTarget;
    const previewer = buildingViewerContainerRef.current;
    const exportViewer = exportViewerContainerRef.current?.parentElement;
    if (!exportViewer || !previewer) {
      return;
    }

    if (!separator.style.gridColumn || separator.style.gridColumn === "3 / span 1") {
      previewer.style.visibility = "hidden";
      separator.style.gridColumn = "1 / span 1";
      exportViewer.style.gridColumn = "2 / span 3";
    } else {
      previewer.style.visibility = "visible";
      separator.style.gridColumn = "3 / span 1";
      exportViewer.style.gridColumn = "4 / span 1";
    }
    window.dispatchEvent(new Event("resize"));
  };

  useEffect(() => {
    void (async () => {
      buildingDispatch(setLoading(null));

      if (!exportViewerRef.current && exportViewerContainerRef.current) {
        exportViewerRef.current = new THREEViewerLite({
          name: "Export viewer",
          container: exportViewerContainerRef.current,
          options: {
            animate: false,
            cameraSettings: {
              name: "Export camera",
              x: -22,
              y: -950,
              z: 66,
              targetX: 0,
              targetY: 0,
              targetZ: 0,
              upX: 0,
              upY: 0,
              upZ: 1,
              ortho: false,
              fov: 60,
            },
          },
        });

        exportViewerRef.current.scene.axesHelperVisible = true;

        exportViewerRef.current.camera.near = nearClipDistance;
        exportViewerRef.current.camera.far = farClipDistance;
        exportViewerRef.current.camera.updateProjectionMatrix();

        const light = new DirectionalLight(0xffffff, lightIntensityToThreePhysicalLight(1));
        light.position.set(-10000, -10000, 10000);
        light.lookAt(new Vector3(0, 0, 0));
        exportViewerRef.current.scene.staticContent.add(light);

        const ambientLight = new AmbientLight(0xffffff, lightIntensityToThreePhysicalLight(0.1));
        exportViewerRef.current.scene.staticContent.add(ambientLight);

        // Allow full camera polar angle range
        const controls: PerspectiveControls = new PerspectiveControls(
          exportViewerRef.current.camera,
          exportViewerRef.current.container,
          exportViewerRef.current.cameraSettings.targetX,
          exportViewerRef.current.cameraSettings.targetY,
          exportViewerRef.current.cameraSettings.targetZ,
        );
        controls.rotateSpeed = orbitSpeed;
        controls.zoomSpeed = dollySpeed;
        controls.panSpeed = panSpeed;
        controls.maxPolarAngle = Math.PI;
        exportViewerRef.current.setControls(controls);

        exportViewerRef.current.scene.groundPlaneMaterial = new MeshStandardMaterial({
          color: "#808080",
          transparent: true,
          opacity: 0.5,
        });
      }
    })();
  }, [exportViewerRef]);

  const ElevationSelection = () => {
    const [elevations, setElevations] = useState<string[]>([]);
    const [activeElevation, setActiveElevation] = useState<string | null>("");

    const style: React.CSSProperties = {
      display: "inline-block",
      marginLeft: "30px",
    };

    useEffect(() => {
      if (!building) {
        return;
      }

      const elevation = buildingElevationOptionSet(building);
      const elevationOptions = elevation.options;

      const enabledElevations = Object.keys(elevationOptions).filter((valueId) => !elevationOptions[valueId].disabled);
      setElevations(enabledElevations);

      void (async () => {
        const [{ optionSetSelectedOptionId }, { SystemOptionSet }] = await Promise.all([
          import("doc/options/optionSetTools"),
          import("doc/options/SystemOptionSetEnum"),
        ]);
        const elevationValue = optionSetSelectedOptionId(
          building.doc.optionSets,
          building.optionChoices,
          SystemOptionSet.ELEVATION_STYLE,
        );
        setActiveElevation(elevationValue);
      })();
    }, [building]);

    const activateElevation = async (event) => {
      if (!building) {
        return;
      }

      const selectedElevation = event.target.value;

      const [{ SystemOptionSet }, { generateBuilding }] = await Promise.all([
        import("doc/options/SystemOptionSetEnum"),
        import("building-generation"),
      ]);
      const newChoices = { ...building.optionChoices, [SystemOptionSet.ELEVATION_STYLE]: selectedElevation };

      const newBuilding = await generateBuilding({
        name: building.name,
        optionChoices: newChoices,
        doc: building.doc,
      });

      buildingDispatch(setBuilding(newBuilding));
    };

    return (
      <div style={style}>
        <select
          value={activeElevation ?? undefined}
          onChange={async (event) => {
            await activateElevation(event);
          }}
        >
          {elevations.map((elevation) => (
            <option value={elevation} key={elevation}>
              {elevation}
            </option>
          ))}
        </select>
      </div>
    );
  };

  type CameraSetting = "orbitSpeed" | "dollySpeed" | "panSpeed" | "nearClipDistance" | "farClipDistance";
  const changeCameraSetting = (type: CameraSetting, value: number) => {
    if (!exportViewerRef.current) {
      return;
    }

    switch (type) {
      case "orbitSpeed":
        exportViewerRef.current.controls.rotateSpeed = value;
        setOrbitSpeed(value);
        break;
      case "dollySpeed":
        exportViewerRef.current.controls.zoomSpeed = value;
        setDollySpeed(value);
        break;
      case "panSpeed":
        exportViewerRef.current.controls.panSpeed = value;
        setPanSpeed(value);
        break;
      case "nearClipDistance":
        exportViewerRef.current.camera.near = value;
        exportViewerRef.current.camera.updateProjectionMatrix();
        setNearClipDistance(value);
        break;
      case "farClipDistance":
        exportViewerRef.current.camera.far = value;
        exportViewerRef.current.camera.updateProjectionMatrix();
        setFarClipDistance(value);
        break;
    }
  };

  const debugContext: DebugContextProps = {
    faceVerticesEnabled: debugFaceVerticesEnabled,
    faceNormalsEnabled: debugFaceNormalsEnabled,
  };

  return (
    <>
      <Head>
        <title>{getPageTitle("Export Debugger")}</title>
      </Head>

      <div className={styles.gridContainer}>
        <div className={styles.exportMenuBar}>
          <div className={styles.openButton}>
            <SplitButton
              size="small"
              onClick={openBuilding}
              innerEl="Open building"
              menuItems={[
                <div key="openConfiguration" onClick={openConfiguration}>
                  Configuration
                </div>,
              ]}
            />
          </div>
          {(building || configuration) && (
            <>
              &nbsp;
              <div className={styles.visualizeButton}>
                <button onClick={exportBuildingGLTFIntoView}>Visualize</button>
              </div>
              &nbsp;
              <button onClick={downloadBuilding}>Download</button>
              {building && <ElevationSelection />}
              <ToggleItem
                style={{ display: "inline-block", marginLeft: "30px" }}
                name="Geometry from material"
                enabled={Object.values(state.exportOptions.materialGeometryGroups).some(Boolean)}
                onChange={() =>
                  dispatch({
                    type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                    update: {
                      materialGeometryGroups: Object.values(state.exportOptions.materialGeometryGroups).some(Boolean)
                        ? emptyBuildingExportGroupsSelection
                        : defaultBuildingExportGroupsSelection,
                    },
                  })
                }
              />
              <DropdownMenu className="right pushRight" title="Debug" canToggleAll>
                <>
                  {debugCallbackTypes.map((debugCallbackType) => (
                    <ToggleItem
                      key={debugCallbackType}
                      name={DebugCallbackType[debugCallbackType]}
                      enabled={selectedDebugCallbacks[debugCallbackType]}
                      onChange={() => {
                        setSelectedDebugCallbacks({
                          ...selectedDebugCallbacks,
                          [debugCallbackType]: !selectedDebugCallbacks[debugCallbackType],
                        });
                      }}
                    />
                  ))}
                </>
              </DropdownMenu>
              <DropdownMenu className="right" title="Options">
                <ToggleItem
                  name="Export for glTF viewers"
                  enabled={state.exportOptions.exportForGLTFViewers}
                  onChange={() =>
                    dispatch({
                      type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                      update: { exportForGLTFViewers: !state.exportOptions.exportForGLTFViewers },
                    })
                  }
                />
                <div>
                  <label>Tiling size: </label>
                  <input
                    type="number"
                    step={0.1}
                    min={0.01}
                    max={10.0}
                    value={state.exportOptions.tilingSize}
                    onChange={(e) =>
                      dispatch({
                        type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                        update: { tilingSize: e.currentTarget.valueAsNumber },
                      })
                    }
                  />
                </div>
                <div>
                  <label>Tiling texel density: </label>
                  <input
                    type="number"
                    step={0.1}
                    min={0.01}
                    max={10.0}
                    value={state.exportOptions.tilingTexelDensity}
                    onChange={(e) =>
                      dispatch({
                        type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                        update: { tilingTexelDensity: e.currentTarget.valueAsNumber },
                      })
                    }
                  />
                </div>
                <hr />
                <ToggleItem
                  name="Process geometry"
                  enabled={state.exportOptions.processGeometry ?? false}
                  onChange={() =>
                    dispatch({
                      type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                      update: { processGeometry: !state.exportOptions.processGeometry },
                    })
                  }
                />
                <ToggleItem
                  name="Log export timings"
                  enabled={state.exportOptions.logExportTimings ?? false}
                  onChange={() =>
                    dispatch({
                      type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                      update: { logExportTimings: !state.exportOptions.logExportTimings },
                    })
                  }
                />
                <ToggleItem
                  name="Warn mask-only materials"
                  enabled={state.exportOptions.warnMaskOnlyMaterials ?? false}
                  onChange={() =>
                    dispatch({
                      type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                      update: { warnMaskOnlyMaterials: !state.exportOptions.warnMaskOnlyMaterials },
                    })
                  }
                />
                <hr />
                <ToggleItem
                  name="Generate missing normals"
                  enabled={state.exportOptions.generateMissingNormals ?? false}
                  onChange={() =>
                    dispatch({
                      type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                      update: { generateMissingNormals: !state.exportOptions.generateMissingNormals },
                    })
                  }
                />
                <hr />
                <ToggleItem
                  name="Generate material within faces"
                  enabled={state.exportOptions.materialWithinFaces ?? false}
                  onChange={() =>
                    dispatch({
                      type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                      update: { materialWithinFaces: !state.exportOptions.materialWithinFaces },
                    })
                  }
                />
                <ToggleItem
                  name="Merge coplanar mesh faces"
                  enabled={state.exportOptions.mergeCoplanarMaterialFaces}
                  onChange={() =>
                    dispatch({
                      type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                      update: { mergeCoplanarMaterialFaces: !state.exportOptions.mergeCoplanarMaterialFaces },
                    })
                  }
                />
                <ToggleItem
                  name="Exclude material base"
                  enabled={state.exportOptions.excludeMaterialBase}
                  onChange={() =>
                    dispatch({
                      type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                      update: { excludeMaterialBase: !state.exportOptions.excludeMaterialBase },
                    })
                  }
                />
                <ToggleItem
                  name="Miter exterior walls"
                  enabled={state.exportOptions.miterExteriorWalls}
                  onChange={() =>
                    dispatch({
                      type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                      update: { miterExteriorWalls: !state.exportOptions.miterExteriorWalls },
                    })
                  }
                />
                <ToggleItem
                  name="Expand cells at inside corner miters"
                  enabled={state.exportOptions.expandCellsAtInsideCornerMiters}
                  onChange={() =>
                    dispatch({
                      type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                      update: { expandCellsAtInsideCornerMiters: !state.exportOptions.expandCellsAtInsideCornerMiters },
                    })
                  }
                />
                <div>
                  Filter geometry generation
                  <input
                    type="text"
                    placeholder="Debug ids, comma-separated"
                    onChange={(event) => setGeometryGenerationDebugIdFilter(event.target.value)}
                  />
                </div>
                <div>
                  Filter face categories
                  <input
                    type="text"
                    placeholder="Face categories, comma-separated"
                    onChange={(event) => setGeometryGenerationFaceCategoryFilter(event.target.value)}
                  />
                </div>
                <hr />
                <ToggleItem
                  name="Constrained Delaunay Triangulation"
                  enabled={state.exportOptions.triangulationStrategy === TriangulationStrategy.CONSTRAINED_DELAUNAY}
                  onChange={() => {
                    const newTriangulationStrategy =
                      state.exportOptions.triangulationStrategy === TriangulationStrategy.CONSTRAINED_DELAUNAY
                        ? TriangulationStrategy.EARCUT
                        : TriangulationStrategy.CONSTRAINED_DELAUNAY;
                    dispatch({
                      type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                      update: { triangulationStrategy: newTriangulationStrategy },
                    });
                  }}
                />
                {state.exportOptions.triangulationStrategy === TriangulationStrategy.CONSTRAINED_DELAUNAY && (
                  <div>
                    Divide length:
                    <input
                      type="number"
                      min={0}
                      value={state.exportOptions.faceDivideEdgeLength}
                      onChange={(e) =>
                        dispatch({
                          type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                          update: { faceDivideEdgeLength: e.currentTarget.valueAsNumber },
                        })
                      }
                    />
                  </div>
                )}
                <div>
                  Vertex welding tolerance:
                  <input
                    type="number"
                    min={0}
                    value={state.exportOptions.vertexWeldingTolerance}
                    onChange={(e) =>
                      dispatch({
                        type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                        update: { vertexWeldingTolerance: e.currentTarget.valueAsNumber },
                      })
                    }
                  />
                </div>

                <hr />
                <div className={styles.optionSection}>Coplanarity distance tolerance</div>
                <div>
                  Coarse:
                  <input
                    type="number"
                    min={0}
                    value={state.exportOptions.coarseCoplanarDistTolerance}
                    onChange={(e) =>
                      dispatch({
                        type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                        update: { coarseCoplanarDistTolerance: e.currentTarget.valueAsNumber },
                      })
                    }
                  />
                </div>
                <div>
                  Fine:
                  <input
                    type="number"
                    min={0}
                    value={state.exportOptions.fineCoplanarDistTolerance}
                    onChange={(e) =>
                      dispatch({
                        type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                        update: { fineCoplanarDistTolerance: e.currentTarget.valueAsNumber },
                      })
                    }
                  />
                </div>
                <hr />
                <div className={styles.optionSection}>Simplification threshold</div>
                <div>
                  Coarse dist:
                  <input
                    type="number"
                    min={0}
                    value={state.exportOptions.coarseSimplificationDistSqThreshold}
                    onChange={(e) =>
                      dispatch({
                        type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                        update: { coarseSimplificationDistSqThreshold: e.currentTarget.valueAsNumber },
                      })
                    }
                  />
                </div>
                <div>
                  Fine dist:
                  <input
                    type="number"
                    min={0}
                    value={state.exportOptions.fineSimplificationDistSqThreshold}
                    onChange={(e) =>
                      dispatch({
                        type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                        update: { fineSimplificationDistSqThreshold: e.currentTarget.valueAsNumber },
                      })
                    }
                  />
                </div>
                <div>
                  Coarse angle:
                  <input
                    type="number"
                    min={0}
                    value={state.exportOptions.coarseSimplificationAngleThreshold}
                    onChange={(e) =>
                      dispatch({
                        type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                        update: { coarseSimplificationAngleThreshold: e.currentTarget.valueAsNumber },
                      })
                    }
                  />
                </div>
                <div>
                  Fine angle:
                  <input
                    type="number"
                    min={0}
                    value={state.exportOptions.fineSimplificationAngleThreshold}
                    onChange={(e) =>
                      dispatch({
                        type: ExportDebuggerActionType.SET_EXPORT_OPTION,
                        update: { fineSimplificationAngleThreshold: e.currentTarget.valueAsNumber },
                      })
                    }
                  />
                </div>
              </DropdownMenu>
            </>
          )}
        </div>
        <div className={styles.debugMenuBar}>
          {hasExport && (
            <>
              <DropdownMenu className="right pushRight" title="Display" canToggleAll={false}>
                <div>
                  Point
                  <input type="text" placeholder="x,y,z" onChange={(event) => displayPoint(event.target.value)} />
                </div>
                <hr />
                <div>
                  Walls by id:
                  <input
                    type="text"
                    placeholder="Wall ids, comma-separated"
                    onChange={(event) => displayWalls(event.target.value)}
                  />
                </div>
              </DropdownMenu>
              <DropdownMenu className="right" title="Debug View">
                <div>
                  <label>Orbit Speed: </label>
                  <input
                    type="number"
                    value={orbitSpeed}
                    step={0.1}
                    min={0.01}
                    max={10.0}
                    onChange={(e) => changeCameraSetting("orbitSpeed", e.currentTarget.valueAsNumber)}
                  />
                </div>
                <div>
                  <label>Dolly Speed: </label>
                  <input
                    type="number"
                    value={dollySpeed}
                    step={0.1}
                    min={0.01}
                    max={10.0}
                    onChange={(e) => changeCameraSetting("dollySpeed", e.currentTarget.valueAsNumber)}
                  />
                </div>
                <div>
                  <label>Pan Speed: </label>
                  <input
                    type="number"
                    value={panSpeed}
                    step={0.1}
                    min={0.01}
                    max={10.0}
                    onChange={(e) => changeCameraSetting("panSpeed", e.currentTarget.valueAsNumber)}
                  />
                </div>
                <div>
                  <label>Near Clip: </label>
                  <input
                    type="number"
                    value={nearClipDistance}
                    min={1.0}
                    max={defaultCameraSettings.cameraFar}
                    onChange={(e) => changeCameraSetting("nearClipDistance", e.currentTarget.valueAsNumber)}
                  />
                </div>
                <div>
                  <label>Far Clip: </label>
                  <input
                    type="number"
                    value={farClipDistance}
                    min={1.0}
                    max={defaultCameraSettings.cameraFar}
                    onChange={(e) => changeCameraSetting("farClipDistance", e.currentTarget.valueAsNumber)}
                  />
                </div>
                <hr />
                <ToggleItem name="Wireframe" enabled={wireframeEnabled} onChange={toggleWireframe} />
                <ToggleItem name="Groundplane" enabled={groundplaneEnabled} onChange={toggleGroundplane} />
                <ToggleItem name="Vertex normals" enabled={vertexNormalsEnabled} onChange={toggleVertexNormals} />
                <hr />
                <ToggleItem
                  name="World-space normal map"
                  enabled={debugModeMap === "normal"}
                  onChange={() => toggleDebugModeMap("normal")}
                />
                <ToggleItem
                  name="World-space UV map"
                  enabled={debugModeMap === "uv"}
                  onChange={() => toggleDebugModeMap("uv")}
                />
                {debugModeMap === "uv" && (
                  <span>
                    UV scale:
                    <input
                      type="number"
                      value={debugUvScale}
                      onChange={(e) => {
                        worldSpaceUVMaterial.uniforms.scale.value = e.currentTarget.valueAsNumber;
                        setDebugUvScale(e.currentTarget.valueAsNumber);
                        requestRender();
                      }}
                    />
                  </span>
                )}
                <ToggleItem
                  name="Winding"
                  enabled={debugModeMap === "winding"}
                  onChange={() => toggleDebugModeMap("winding")}
                />
                <hr />
                <ToggleItem
                  name="Debug face vertices"
                  enabled={debugFaceVerticesEnabled}
                  onChange={toggleDebugFaceVertices}
                />
                <ToggleItem
                  name="Debug face normals"
                  enabled={debugFaceNormalsEnabled}
                  onChange={toggleDebugFaceNormals}
                />
                <ToggleItem
                  name="Debug info"
                  enabled={debugInfoEnabled}
                  onChange={() => setDebugInfoEnabled(!debugInfoEnabled)}
                />
              </DropdownMenu>
            </>
          )}
        </div>
        <div className={styles.viewerContainer + " " + (configuration ? "" : "invisible")}>
          <ExportConfigurationViewer configuration={configuration} />
        </div>
        <div
          className={styles.viewerContainer + " " + (configuration ? "invisible" : "")}
          ref={buildingViewerContainerRef}
          id={DEBUG_EXPORT_PAGE_WRAPPER_ID}
        >
          {loading && <Loading />}
          <DebugBuildingViewer viewerSystem={buildingViewerSystemRef} />
          <SceneStateHUD />

          <div className={styles.renderStageFilterControlsWrapper}>
            <View3DFilterControls
              filter={viewFilter}
              defaultFilter={exporterViewerDefaultRenderStageFilter}
              onChange={(f) => buildingDispatch(setViewFilter(f))}
            />
          </div>
        </div>
        <div className={styles.viewerSeparator} onClick={toggleSeparator} />
        <div className={styles.exportViewerContainer}>
          <DebugContext.Provider value={debugContext}>
            <DebugInfo
              className={styles.styledDebugInfo}
              debugInfo={debugInfo}
              enabled={debugInfoEnabled}
              viewerRef={exportViewerRef}
            />
          </DebugContext.Provider>
          <FullScreenBox ref={exportViewerContainerRef} />
          {exportLoadingState && <Loading />}
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute("/debug/export")({
  component: withAuthentication(withCustomizer(DebugExportPage, defaultExporterView3DFilter), { superAdmin: true }),
});
